'use client';
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';
import { usePathname } from 'next/navigation';

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  is_active: boolean;
}

interface CurrencyContextType {
  currency: Currency;
  currencies: Currency[];
  setCurrency: (currency: Currency) => void;
  formatAmount: (value: number) => string;
  loading: boolean;
}

const DEFAULT_CURRENCY: Currency = {
  id: '',
  code: 'BRL',
  name: 'Real Brasileiro',
  symbol: 'R$',
  is_active: true,
};

const CURRENCY_STORAGE_KEY = 'betvault_currency';
const CURRENCY_LIST_STORAGE_KEY = 'betvault_currency_list';

const CurrencyContext = createContext<CurrencyContextType>({
  currency: DEFAULT_CURRENCY,
  currencies: [],
  setCurrency: () => {},
  formatAmount: (v) => `R$ ${v.toFixed(2)}`,
  loading: false,
});

function getStoredCurrency(): Currency | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CURRENCY_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStoredCurrency(currency: Currency) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, JSON.stringify(currency));
  } catch {
    return;
  }
}

function getStoredCurrencies(): Currency[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(CURRENCY_LIST_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveStoredCurrencies(currencies: Currency[]) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(CURRENCY_LIST_STORAGE_KEY, JSON.stringify(currencies));
  } catch {
    return;
  }
}

function resolvePreferredCurrency(list: Currency[], preferred?: Currency | null): Currency {
  if (preferred) {
    const matchedCurrency = list.find((currency) => currency.code === preferred.code);
    if (matchedCurrency) {
      return matchedCurrency;
    }
  }

  const brlCurrency = list.find((currency) => currency.code === 'BRL');
  return brlCurrency || list[0] || preferred || DEFAULT_CURRENCY;
}

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
  const [currency, setCurrencyState] = useState<Currency>(DEFAULT_CURRENCY);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = useMemo(() => createClient(), []);
  const pathname = usePathname();
  const isAuthPublicPath =
    pathname === '/login' ||
    pathname === '/forgot-password' ||
    pathname === '/update-password' ||
    pathname.startsWith('/auth/');

  // Load currencies from DB and restore user preference
  useEffect(() => {
    let cancelled = false;

    async function init() {
      const storedCurrency = getStoredCurrency();
      const storedCurrencies = getStoredCurrencies();

      if (storedCurrencies.length > 0) {
        setCurrencies(storedCurrencies);
        setCurrencyState(resolvePreferredCurrency(storedCurrencies, storedCurrency));
        setLoading(false);
      } else if (storedCurrency) {
        setCurrencyState(storedCurrency);
        setLoading(false);
      } else {
        setLoading(true);
      }

      try {
        const sessionPromise = isAuthPublicPath
          ? Promise.resolve<string | null>(null)
          : supabase.auth.getSession().then((result: any) => result.data.session?.user?.id || null);

        const [currencyRes, userId] = await Promise.all([
          supabase.from('currencies').select('*').eq('is_active', true).order('code'),
          sessionPromise,
        ]);

        const list: Currency[] = (currencyRes.data || []) as Currency[];
        const resolvedList = list.length > 0 ? list : storedCurrencies;

        if (!cancelled) {
          setCurrencies(resolvedList);
          if (list.length > 0) {
            saveStoredCurrencies(list);
          }
        }

        if (isAuthPublicPath) {
          if (!cancelled) {
            setCurrencyState(resolvePreferredCurrency(resolvedList, storedCurrency));
            setLoading(false);
          }
          return;
        }

        if (userId) {
          const { data: prefs } = await supabase
            .from('user_preferences')
            .select('currency_id, currencies(id, code, name, symbol, is_active)')
            .eq('user_id', userId)
            .single();

          if (prefs?.currencies) {
            const dbCurrency = prefs.currencies as unknown as Currency;
            if (!cancelled) {
              setCurrencyState(resolvePreferredCurrency(resolvedList, dbCurrency));
            }
            saveStoredCurrency(dbCurrency);
            return;
          }
        }

        if (!cancelled) {
          setCurrencyState(resolvePreferredCurrency(resolvedList, storedCurrency));
        }
      } catch {
        if (!cancelled) {
          if (storedCurrencies.length > 0) {
            setCurrencies(storedCurrencies);
          }
          setCurrencyState(resolvePreferredCurrency(storedCurrencies, storedCurrency));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      cancelled = true;
    };
  }, [isAuthPublicPath, supabase]);

  const setCurrency = useCallback(
    async (newCurrency: Currency) => {
      setCurrencyState(newCurrency);
      saveStoredCurrency(newCurrency);

      // Persist to DB if user is logged in
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const user = session?.user;
        if (user) {
          await supabase.from('user_preferences').upsert(
            {
              user_id: user.id,
              currency_id: newCurrency.id,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'user_id' }
          );
        }
      } catch {
        // Silently fail — localStorage already saved
      }
    },
    [supabase]
  );

  const formatAmount = useCallback(
    (value: number): string => {
      const sym = currency.symbol;
      const formatted = Math.abs(value).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return value < 0 ? `-${sym} ${formatted}` : `${sym} ${formatted}`;
    },
    [currency]
  );

  return (
    <CurrencyContext.Provider value={{ currency, currencies, setCurrency, formatAmount, loading }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  return useContext(CurrencyContext);
}
